import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AssignableOrgRole } from '@hangar/shared';
import {
  createClientOrg, createInvitation, listClientOrgs, setUpClientProviders, listMembers, listPendingInvitations, removeMember, resendInvitation, revokeInvitation, updateMember,
  type ClientOrgInput, type InviteInput,
} from './endpointsMembers';
import type { ClientOrg, Invitation, Member } from './schemasMembers';

/* ---------- Members, invitations and client orgs ---------- */

export const membersKeys = {
  members: ['members'] as const,
  invitations: ['invitations', 'pending'] as const,
  clients: ['platform-orgs'] as const,
};

/** The org's members (admins only: pass `enabled: false` for other roles, the API answers 403). */
export const useMembers = (enabled = true) => useQuery({ queryKey: membersKeys.members, queryFn: listMembers, enabled });
export const usePendingInvitations = (enabled = true) => useQuery({ queryKey: membersKeys.invitations, queryFn: listPendingInvitations, enabled });

export function useMemberMutations() {
  const qc = useQueryClient();
  const settle = () => void qc.invalidateQueries({ queryKey: membersKeys.members });
  const update = useMutation({
    mutationFn: (v: { userId: string; role: AssignableOrgRole }) => updateMember(v.userId, { role: v.role }),
    onSuccess: (m) => {
      qc.setQueryData<Member[]>(membersKeys.members, (old) => old?.map((x) => (x.user.id === m.user.id ? m : x)));
      settle();
    },
  });
  const remove = useMutation({
    mutationFn: (userId: string) => removeMember(userId),
    onSuccess: (_none, userId) => {
      qc.setQueryData<Member[]>(membersKeys.members, (old) => old?.filter((x) => x.user.id !== userId));
      settle();
    },
  });
  return { update, remove };
}

export function useInvitationMutations() {
  const qc = useQueryClient();
  const put = (i: Invitation) => qc.setQueryData<Invitation[]>(membersKeys.invitations, (old) => (old ? [i, ...old.filter((x) => x.id !== i.id)] : old));
  const settle = () => void qc.invalidateQueries({ queryKey: membersKeys.invitations });
  const invite = useMutation({
    mutationFn: (v: { body: InviteInput; idempotencyKey: string }) => createInvitation(v.body, v.idempotencyKey),
    onSuccess: (i) => {
      put(i);
      settle();
    },
  });
  const resend = useMutation({
    mutationFn: (invitationId: string) => resendInvitation(invitationId),
    onSuccess: (i) => {
      put(i);
      settle();
    },
  });
  const revoke = useMutation({
    mutationFn: (invitationId: string) => revokeInvitation(invitationId),
    onSuccess: (_none, invitationId) => {
      qc.setQueryData<Invitation[]>(membersKeys.invitations, (old) => old?.filter((x) => x.id !== invitationId));
      settle();
    },
  });
  return { invite, resend, revoke };
}

/** Client orgs (platform operators only). */
export const useClientOrgs = (enabled = true) => useQuery({ queryKey: membersKeys.clients, queryFn: listClientOrgs, enabled });

export function useCreateClientOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ClientOrgInput) => createClientOrg(body),
    onSuccess: (created) => {
      qc.setQueryData<ClientOrg[]>(membersKeys.clients, (old) => (old ? [created.org, ...old] : old));
      void qc.invalidateQueries({ queryKey: membersKeys.clients });
    },
  });
}

/** Re-runs a client's provider setup; the client list refetches to show the accounts it left. */
export function useSetUpClientProviders() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orgId: string) => setUpClientProviders(orgId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: membersKeys.clients }),
  });
}
